import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import EmailCapture from '../components/EmailCapture';
import SymptomChecker from '../components/SymptomChecker';
import { stacks } from '../data/stacks';
import { bloodTests } from '../data/bloodTests';

const womenTiles = [
  { num: '30s', title: 'Fertility, thyroid & early signs', to: '/women-30s' },
  { num: '40s', title: 'Perimenopause, HRT & brain fog', to: '/women-40s', active: true },
  { num: '50s', title: 'Menopause & bone density', to: '/women-50s' },
  { num: '60s', title: 'Post-menopause & long-term health', to: '/women-60s' },
];

const menTiles = [
  { num: '30s', title: 'Testosterone baseline & cortisol', to: '/men-30s' },
  { num: '40s', title: 'Andropause, energy & weight gain', to: '/men-40s' },
  { num: '50s', title: 'TRT explained & metabolic health', to: '/men-50s' },
  { num: '60s', title: 'Maintaining health & quality of life', to: '/men-60s' },
];

// Two separate arrays (not one combined list) so women's and men's decade
// cards render as two genuinely independent rows/carousels — never a single
// mixed sequence a mobile swipe could blur together.
const womenGuideCards = [
  { decade: '30s', title: "Women in their 30s", topics: "Fertility hormones, thyroid, early perimenopause signs, mood and energy", to: "/women-30s" },
  { decade: '40s', title: "Women in their 40s", topics: "Perimenopause, oestrogen decline, HRT explained, weight, sleep & brain fog", to: "/women-40s" },
  { decade: '50s', title: "Women in their 50s", topics: "Menopause, bone density, heart health, testosterone in women", to: "/women-50s" },
  { decade: '60s', title: "Women in their 60s", topics: "Post-menopause health, bone density, cardiovascular risk and genitourinary symptoms", to: "/women-60s" },
];
const menGuideCards = [
  { decade: '30s', title: "Men in their 30s", topics: "Testosterone starts declining, cortisol and stress, sleep hormones & fertility", to: "/men-30s" },
  { decade: '40s', title: "Men in their 40s", topics: "Andropause, testosterone decline, energy, body composition & mood", to: "/men-40s" },
  { decade: '50s', title: "Men in their 50s", topics: "TRT explained, thyroid, cardiovascular health & when your GP says you're fine", to: "/men-50s" },
  { decade: '60s', title: "Men in their 60s", topics: "Maintaining health and quality of life. TRT, cardiovascular risk and muscle mass", to: "/men-60s" },
];

const symptoms = [
  { q: "Why am I so tired all the time?", sub: "Fatigue & energy", to: "/symptoms" },
  { q: "Why can't I sleep?", sub: "Sleep disruption", to: "/symptoms" },
  { q: "Why am I gaining weight around my middle?", sub: "Weight & metabolism", to: "/symptoms" },
  { q: "Why do I feel anxious for no reason?", sub: "Mood & anxiety", to: "/symptoms" },
  { q: "Why has my sex drive disappeared?", sub: "Libido", to: "/symptoms" },
  { q: "Why is my hair falling out?", sub: "Hair & skin", to: "/symptoms" },
  { q: "Why do I feel so flat?", sub: "Low mood & motivation", to: "/symptoms" },
  { q: "Why can't I concentrate?", sub: "Brain fog & focus", to: "/symptoms" },
  { q: "Why do I feel so hot at night?", sub: "Night sweats & temperature", to: "/symptoms" },
];

export default function Home() {
  const featuredStacks = stacks.slice(0, 4);

  return (
    <>
      <Helmet defer={false}>
        <title>Hormonely — Hormone health, by decade</title>
        <meta name="description" content="Plain English hormone health information for men and women in their 40s, 50s and beyond. Decade guides, symptom library, home blood tests and targeted supplement stacks." />
      </Helmet>

      {/* HERO */}
      <div className="hero">
        <div className="container" style={{ display: 'contents' }}>
          <div className="hero__left container" style={{ maxWidth: 'none', paddingRight: '64px' }}>
            <div className="eyebrow hero__eyebrow">Hormone health · By decade · Plain English</div>
            <h1 className="hero__headline">
              Your hormones change <em>every decade.</em> Most people don't know until something goes wrong.
            </h1>
            <p className="hero__body">
              Practical, honest information about what's happening in your body — and what to do about it. For men and women in their 40s, 50s and beyond.
            </p>
            <div className="hero__split">
              <Link to="/women" className="hero__split-btn hero__split-btn--women">I'm a woman</Link>
              <Link to="/men" className="hero__split-btn hero__split-btn--men">I'm a man</Link>
            </div>
            <EmailCapture />
          </div>

          <div className="hero__right">
            <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '400px' }}>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--clay-lt)' }}>Women</div>
                <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>Men</div>
              </div>
              {/* Grid: decades run down, sex across */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {womenTiles.map((t, i) => (
                  <Link
                    key={`w${i}`}
                    to={t.to}
                    style={{
                      display: 'block',
                      background: t.active ? 'rgba(196,120,90,0.22)' : 'rgba(255,255,255,0.05)',
                      border: t.active ? '1px solid rgba(196,120,90,0.45)' : '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      padding: '14px 16px',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      gridColumn: 1,
                      gridRow: i + 1,
                    }}
                  >
                    <div style={{ fontFamily: 'var(--serif)', fontSize: '28px', color: t.active ? 'var(--clay-lt)' : 'rgba(255,255,255,0.2)', lineHeight: 1, marginBottom: '6px' }}>{t.num}</div>
                    <div style={{ fontSize: '11px', color: t.active ? 'var(--white)' : 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{t.title}</div>
                  </Link>
                ))}
                {menTiles.map((t, i) => (
                  <Link
                    key={`m${i}`}
                    to={t.to}
                    style={{
                      display: 'block',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      padding: '14px 16px',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      gridColumn: 2,
                      gridRow: i + 1,
                    }}
                  >
                    <div style={{ fontFamily: 'var(--serif)', fontSize: '28px', color: 'rgba(255,255,255,0.2)', lineHeight: 1, marginBottom: '6px' }}>{t.num}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{t.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TRUST BAR */}
      <div className="trust-bar">
        <div className="trust-bar__inner">
          {['Plain English. No jargon.', 'Both sexes. All decades.', 'No scare tactics. Ever.', 'Information only — not medical advice.'].map((t, i) => (
            <div className="trust-item" key={i}>
              <div className="trust-dot"></div>
              <span className="trust-text">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* DECADE GUIDES */}
      <section className="section section--white">
        <div className="container">
          <div className="section__header">
            <div className="eyebrow">The Decade Guides</div>
            <h2 className="section-title" style={{ marginTop: '12px' }}>What's actually happening <em>in your body</em></h2>
            <p className="section__body">Long-form guides written for real people, not clinicians. Each covers what's changing, what symptoms to expect, what tests to consider, and what actually helps.</p>
          </div>
          {/* Two independent labelled rows — never one combined list — so
              women's and men's decade cards can't blur together on a mobile
              swipe the way a single 6-card carousel did. */}
          <div className="guides-group">
            <div className="guides-group__label">Women</div>
            <div className="guides-grid">
              {womenGuideCards.map((g, i) => (
                <Link key={i} to={g.to} className="guide-card">
                  <span className="guide-card__decade">{g.decade}</span>
                  <div className="guide-card__title">{g.title}</div>
                  <div className="guide-card__topics">{g.topics}</div>
                  <span className="guide-card__arrow">→</span>
                </Link>
              ))}
            </div>
          </div>
          <div className="guides-group">
            <div className="guides-group__label">Men</div>
            <div className="guides-grid">
              {menGuideCards.map((g, i) => (
                <Link key={i} to={g.to} className="guide-card">
                  <span className="guide-card__decade">{g.decade}</span>
                  <div className="guide-card__title">{g.title}</div>
                  <div className="guide-card__topics">{g.topics}</div>
                  <span className="guide-card__arrow">→</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SYMPTOMS */}
      <section className="section section--dark">
        <div className="container">
          <div className="section__header">
            <div className="eyebrow">Symptoms Library</div>
            <h2 className="section-title" style={{ marginTop: '12px' }}>Start with how you <em>feel</em></h2>
            <p className="section__body">People search symptoms, not conditions. Find yours below — each page explains the hormonal cause and what to do about it.</p>
          </div>
          <div className="symptoms-grid">
            {symptoms.map((s, i) => (
              <Link key={i} to={s.to} className="symptom-pill">
                {s.q}
                <span>{s.sub}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI CHECKER */}
      <section className="checker-section">
        <div className="container">
          <div className="checker-layout">
            <div className="checker-left">
              <div className="eyebrow">Ask Hormonely</div>
              <h2 className="section-title" style={{ marginTop: '12px', marginBottom: '16px' }}>Five questions. A clear <em>next step.</em></h2>
              <p className="section__body">Tell us your sex, your decade and what you have noticed. We'll suggest questions to take to your GP or pharmacist, and the guide worth reading first.</p>
              <p className="checker-note">Powered by the Anthropic API. Provides information only — not a diagnosis. Always speak to your GP.</p>
            </div>
            <SymptomChecker />
          </div>
        </div>
      </section>

      {/* STACKS */}
      <section className="section section--warm">
        <div className="container">
          <div className="section__header">
            <div className="eyebrow">Stacks by Hormonely</div>
            <h2 className="section-title" style={{ marginTop: '12px' }}>Stacks by <em>Hormonely.</em></h2>
            <div style={{ marginBottom: '40px' }}>
              <p className="section__body">Food supplements made in the UK under GMP standards, delivered on subscription. Food supplements should not replace a varied diet.</p>
              <div style={{ display: 'flex', gap: '20px', marginTop: '14px', flexWrap: 'wrap' }}>
                {['Free UK delivery', 'No minimum orders', 'Cancel any time'].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--clay)', flexShrink: 0 }}></div>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--stone)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="stacks-grid">
            {featuredStacks.map((s) => (
              <Link key={s.slug} to={`/stacks/${s.slug}`} className="stack-card">
                <div className="stack-card__badge">{s.badge}</div>
                <div className="stack-card__name">{s.name}</div>
                <div className="stack-card__for">{s.for}</div>
                <div className="stack-card__price">
                  £{s.subscriptionPrice} <span>/{s.billingPeriod === 'monthly' ? 'month' : s.billingPeriod.replace('every ', '')}</span>
                </div>
              </Link>
            ))}
          </div>
          <div style={{ marginTop: '32px' }}>
            <Link to="/stacks" className="btn-primary">View all 8 stacks</Link>
          </div>
        </div>
      </section>

      {/* BLOOD TESTS */}
      <section className="section section--white">
        <div className="container">
          <div className="tests-layout">
            <div>
              <div className="eyebrow">Blood Tests</div>
              <h2 className="section-title" style={{ marginTop: '12px', marginBottom: '16px' }}>Know before you <em>guess.</em></h2>
              <p className="section__body">Test before you supplement or seek treatment. Every panel below is available at home — finger-prick sample, posted back, results in 48 hours.</p>
              <p style={{ fontSize: '13px', color: 'var(--stone)', marginTop: '12px', marginBottom: '28px' }}>Hormonely readers receive 10% off all Medichecks panels.</p>
              <Link to="/blood-tests" className="btn-primary">View all panels</Link>
            </div>
            <div className="test-list">
              {bloodTests.slice(0, 4).map((t, i) => (
                <a key={i} href={t.url} target="_blank" rel="noopener noreferrer" className="test-item">
                  <div>
                    <div className="test-item__name">{t.name}</div>
                    <div className="test-item__desc">{t.desc}</div>
                  </div>
                  <div className="test-item__price">{t.price}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
